import React from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "./auth";

function _hasScope(perms, scope) {
  if (!scope) return true;
  if (!Array.isArray(perms)) return true;   // permissions not loaded yet
  if (perms.length === 0) return false;
  if (perms.includes("*")) return true;
  if (perms.includes(scope)) return true;
  // Walk every parent scope — a `finance.*` grant should also unlock
  // `finance.emails.view` etc.
  const parts = scope.split(".");
  for (let i = 1; i <= parts.length; i++) {
    const s = parts.slice(0, i).join(".");
    if (perms.includes(s) || perms.includes(`${s}.*`)) return true;
  }
  // Any child capability implies partial access
  const prefix = `${scope}.`;
  return perms.some(p => p.startsWith(prefix));
}

/**
 * Best-effort URL → permission-scope mapping so we can enforce access
 * without touching every <Route> definition.  We map fine-grained submodule
 * keys where possible (e.g. `/finance/emails` → `finance.emails`) so a
 * revoked submodule blocks URL entry too, not just sidebar visibility.
 */
function _scopeFromPath(pathname) {
  if (!pathname) return null;
  // Master control
  if (pathname === "/master/locations")       return "master.locations";
  if (pathname === "/master/zones")           return "master.zones";
  if (pathname === "/master/schedules")       return "master.schedules";
  if (pathname === "/master/special-dates")   return "master.special_dates";
  if (pathname === "/master/drop-locations")  return "master.drop_locations";
  if (pathname.startsWith("/master/") || pathname === "/master") return "master";
  // Service & Pricing
  if (pathname === "/pricing/service-categories") return "master.service_categories";
  if (pathname === "/pricing/services")           return "master.services";
  if (pathname === "/pricing/waste-types")        return "master.waste_types";
  if (pathname === "/pricing/waste-report")       return "master.waste_report";
  if (pathname === "/pricing/distance")           return "master.distance_pricing";
  if (pathname === "/pricing/charges")            return "master.priority_charges";
  if (pathname === "/pricing/products")           return "master.products";
  if (pathname === "/pricing/packages")           return "master.packages";
  if (pathname === "/pricing/calculator")         return "master.calculator";
  if (pathname.startsWith("/pricing/"))           return "master";
  // Customers
  if (pathname === "/customers")                  return "customers.list";
  if (pathname === "/customers/verify")           return "customers.verify";
  if (pathname === "/customers/referrals/affiliate")    return "customers.referrals_affiliate";
  if (pathname.startsWith("/customers/referrals/partners")) return "customers.referrals_partners";
  if (pathname === "/customers/referrals/performance")  return "customers.referrals_performance";
  if (pathname.startsWith("/customers")) return "customers";
  // CRM
  if (pathname === "/crm/leads")                 return "crm.leads";
  if (pathname === "/crm/contact-submissions")   return "crm.contact_submissions";
  if (pathname === "/crm/tickets")               return "crm.tickets";
  if (pathname === "/crm/chats")                 return "crm.chats";
  if (pathname.startsWith("/crm/"))              return "crm";
  // Operations
  if (pathname === "/operations")                    return "operations.planning";
  if (pathname === "/operations/service-requests")   return "operations.service_requests";
  if (pathname === "/operations/service-requests/daily") return "operations.service_requests_daily";
  if (pathname === "/operations/routes/daily")       return "operations.routes_daily";
  if (pathname === "/operations/routes/new")         return "operations.routes_new";
  if (pathname.startsWith("/operations/routes"))     return "operations.routes_jobs";
  if (pathname === "/live")                          return "operations.live_ops";
  if (pathname.startsWith("/qr"))                    return "operations.qr_inventory";
  if (pathname.startsWith("/disposal"))              return "operations.disposal";
  if (pathname.startsWith("/operations/"))           return "operations";
  // Finance
  if (pathname === "/finance/invoices")         return "finance.invoices";
  if (pathname === "/finance/invoices-pending") return "finance.invoices_pending";
  if (pathname === "/finance/transactions")     return "finance.transactions";
  if (pathname === "/finance/payments")         return "finance.razorpay";
  if (pathname === "/finance/emails")           return "finance.emails";
  if (pathname === "/finance/revenue")          return "finance.revenue";
  if (pathname === "/finance/rollup")           return "finance.rollup";
  if (pathname === "/finance/settings")         return "finance.settings";
  if (pathname === "/admin/manual-payments")    return "finance.manual_upi";
  if (pathname === "/fraud")                    return "finance.fraud";
  if (pathname.startsWith("/finance/"))         return "finance";
  // Workforce / Staff Management
  if (pathname === "/admin/staff-mgmt")               return "workforce.staff_list";
  if (pathname === "/admin/staff-mgmt/new")           return "workforce.staff_create";
  if (pathname === "/admin/staff-mgmt/attendance")    return "workforce.attendance";
  if (pathname === "/admin/staff-mgmt/logs")          return "workforce.break_logs";
  if (pathname === "/admin/staff-mgmt/performance")   return "workforce.performance";
  if (pathname === "/admin/staff-mgmt/payroll")       return "workforce.payroll";
  if (pathname === "/admin/staff-mgmt/roles")         return "workforce.roles";
  if (pathname.startsWith("/admin/staff-mgmt"))       return "workforce";
  if (pathname === "/staff" || pathname.startsWith("/staff/"))   return "workforce.field_staff";
  if (pathname === "/workforce/leaderboard")           return "workforce.leaderboard";
  if (pathname === "/workforce/shifts")                return "workforce.shifts";
  if (pathname === "/workforce/payroll")               return "workforce.payroll";
  if (pathname.startsWith("/workforce/"))              return "workforce";
  if (pathname.startsWith("/fleet"))                   return "workforce.fleet";
  // Reports / Certificates
  if (pathname.startsWith("/analytics"))                return "reports.analytics";
  if (pathname === "/certificates")                     return "certificates.list";
  if (pathname === "/admin/institutional-certs")        return "certificates.institutional";
  if (pathname.startsWith("/training-admin"))           return "certificates.lms_config";
  if (pathname.startsWith("/admin/training-partners"))  return "certificates.training_partners";
  if (pathname === "/training")                         return "certificates.training_public";
  // Settings / Administration / Content Management / System
  if (pathname === "/audit-logs")                       return "settings.audit_logs";
  if (pathname === "/settings")                         return "settings.general";
  if (pathname === "/settings/printer")                 return "settings.printer";
  if (pathname === "/settings/scale")                   return "settings.scale";
  if (pathname === "/settings/chat-bot")                return "settings.chat_bot";
  if (pathname === "/admin/otp-settings")               return "settings.otp";
  if (pathname === "/admin/data-sync")                  return "settings.data_sync";
  if (pathname === "/admin/service-partners")           return "settings.service_partners";
  if (pathname === "/admin/gov-users")                  return "settings.gov_users";
  if (pathname.startsWith("/franchise"))                return "settings.franchise";
  if (pathname === "/admin/content" || pathname.startsWith("/admin/content/"))
    return "settings.publications";
  if (pathname === "/admin/about")                      return "settings.about_editor";
  if (pathname === "/admin/nav-menus")                  return "settings.nav_menus";
  if (pathname === "/admin/careers")                    return "settings.careers";
  if (pathname === "/admin/agencies")                   return "settings.agencies";
  if (pathname === "/admin/registration-certs")         return "settings.registration_certs";
  if (pathname === "/customers/registration-certs")     return "settings.registration_certs";
  if (pathname === "/admin/media")                      return "settings.media";
  if (pathname === "/admin/site-pages")                 return "settings.site_pages";
  if (pathname.startsWith("/settings/"))                return "settings";
  return null;
}

export default function ProtectedRoute({ children, roles, perm }) {
  const { user, bootstrapped, can } = useAuth();
  const location = useLocation();
  if (!bootstrapped) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F6F2]">
        <div className="text-[#5C5A55]">Loading…</div>
      </div>
    );
  }
  // /adentry is intentionally only reachable by direct URL entry. Show a
  // generic 404 instead of auto-redirecting the user there.
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F6F2]">
        <div className="text-center max-w-md px-6">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#8A8780]">404</div>
          <h2 className="text-2xl font-heading mt-2">Page not found</h2>
          <p className="text-sm text-[#5C5A55] mt-3">
            This page requires a signed-in session. Please head back to the home page.
          </p>
          <a href="/" className="inline-flex items-center gap-2 mt-5 h-10 px-4 rounded-md bg-[#2D5A27] hover:bg-[#3A7033] text-white text-sm font-medium">
            Go to home
          </a>
        </div>
      </div>
    );
  }
  if (roles && !can(roles)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F6F2]">
        <div className="text-center">
          <h2 className="text-2xl font-heading">403 — Access denied</h2>
          <p className="text-sm text-[#5C5A55] mt-2">
            Your role ({user.role}) cannot access this page.
          </p>
        </div>
      </div>
    );
  }
  // Fine-grained module-scope check — enforced when the caller supplies a
  // `perm` (e.g. "master", "workforce") OR when the URL falls under one of
  // the known module prefixes (via `_scopeFromPath`).  Superadmin
  // (perms includes "*") always passes.
  const scope = perm || _scopeFromPath(location.pathname);
  if (scope && !_hasScope(user.permissions, scope)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F6F2]">
        <div className="text-center max-w-md px-6">
          <h2 className="text-2xl font-heading">Access revoked</h2>
          <p className="text-sm text-[#5C5A55] mt-2">
            Your role ({user.role}) no longer has access to this module.
            An administrator has removed this capability.
          </p>
        </div>
      </div>
    );
  }
  return children;
}
